<template lang="pug">
.arc-button-drop(v-show="list.length", :class="`${uniq}${(open ? ' opened' : '')} ${route}`")
  button(:class="className", @click="toggle")
    span {{ name.length ? name : 'i\'m drop button' }}
    img.arc-button-drop__icon(src="./img/down.svg", alt="arrow")
  .arc-button-drop__list
    template(v-for="item in list")
      .arc-button-drop__list__item(@click="selected(item)")
        span {{ item.name }}

</template>

<script>
export default {
  name: 'ArcDropButton',
  props: {
    name: {
      type: String,
      default: '',
      required: false,
    },
    outline: {
      type: Boolean,
      default: false,
      required: false,
    },
    outlineGray: {
      type: Boolean,
      default: false,
      required: false,
    },
    dashed: {
      type: Boolean,
      default: false,
      required: false,
    },
    color: {
      type: String,
      default: '',
      required: false,
    },
    list: {
      type: Array,
      default: () => ([]),
      required: false,
    },
    bottom: {
      type: Boolean,
      default: false,
      required: false,
    },
    top: {
      type: Boolean,
      default: false,
      required: false,
    },
    left: {
      type: Boolean,
      default: false,
      required: false,
    },
    right: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data: () => ({
    className: 'arc-button-drop',
    uniq: '',

    open: false,

    route: 'top', // top as default
  }),
  mounted() {
    this.uniq = `abd__${new Date().getTime()}-${Math.floor(Math.random() * 100000)}`;

    if (this.dashed) {
      this.className += '__dashed';
    } else if (this.outline) {
      this.className += '__outline';
    } else if (this.outlineGray) {
      this.className += '__outline-gray';
    } else {
      this.className += '__default';
    }

    if (this.bottom) {
      this.route = 'bottom';
    }

    if (this.left) {
      this.route = 'left';
    }

    if (this.right) {
      this.route = 'right';
    }

    if (this.top) {
      this.route = 'top';
    }
  },
  methods: {
    toggle() {
      if (this.open) {
        this.close();
      } else {
        this.open = true;
        this.addWatcher();
      }
    },
    selected(listItem) {
      this.$emit('action', listItem);
      this.close();
    },

    close() {
      this.rmWatcher();
      this.open = false;
    },

    rmWatcher() {
      document.removeEventListener('click', this.watcher, false);
    },

    addWatcher() {
      document.addEventListener('click', this.watcher, false);
    },

    watcher(e) {
      if (!this.recursiveFindDOM(e.target, this.uniq)) {
        this.close();
      }
    },

    recursiveFindDOM(dom, classKey) {
      const domName = (dom.localName || dom.tagName || dom.nodeName);
      if (domName.toLowerCase() !== 'body') {
        if (dom.classList.value.match(classKey)) {
          return true;
        }
        if (Object.prototype.hasOwnProperty.call(dom, 'parentNode')) {
          return this.recursiveFindDOM(dom.parentNode, classKey);
        }
        return this.recursiveFindDOM(dom.parentElement, classKey);
      }
      return false;
    },
  },
};
</script>

<style lang="scss">
$--font-family: Inter;
$--accent: rgb(255, 234, 0);
$--gray: rgb(246, 246, 246);
$--text: rgb(0, 0, 0);
$--shadow: 2.7px 5.3px 24px 0 rgba(0, 0, 0, 0.12);
$--transition: all .25s ease-in-out;

%btn-drop-clear {
  appearance: none;
  user-select: none;
  outline: none;
  cursor: pointer;
}

%btn-drop-defaults {
  font-family: $--font-family;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;

  color: $--text;

  height: 50px;
  padding: 0 20px;
  border-radius: 8px;

  transition: $--transition;
}

.arc-button-drop {
  position: relative;

  // DEFAULT
  &__default {
    @extend %btn-drop-clear;
    @extend %btn-drop-defaults;
    border: none;
    background-color: $--gray;
    &:hover {
      background-color: $--accent;
    }
  }
  &.opened &__default {
    > * {
      opacity: .5;
    }
    background-color: $--accent;
  }
  // DEFAULT

  // OUTLINE
  &__outline {
    @extend %btn-drop-clear;
    @extend %btn-drop-defaults;
    background: transparent;
    border: solid 1px $--accent;
    &:hover {
      background-color: $--accent;
    }
  }
  &.opened &__outline {
    > * {
      opacity: .5;
    }
    background-color: $--accent;
  }
  // OUTLINE

  // OUTLINE GRAY
  &__outline-gray {
    @extend %btn-drop-clear;
    @extend %btn-drop-defaults;
    background: transparent;
    border: solid 1px $--gray;
    &:hover {
      background-color: $--accent;
      border-color: $--accent;
    }
  }
  &.opened &__outline-gray {
    > * {
      opacity: .5;
    }
    background-color: $--accent;
  }
  // OUTLINE GRAY

  // DASHED
  &__dashed {
    @extend %btn-drop-clear;
    @extend %btn-drop-defaults;
    background: transparent;
    border: 1px dashed rgb(229, 229, 229);
    &:hover {
      background-color: rgb(229, 229, 229);
    }
  }
  &.opened &__dashed {
    > * {
      opacity: .5;
    }
    background-color: rgb(229, 229, 229);
  }
  // DASHED

  // ICON
  &__icon {
    margin-left: 8px;
    transition: $--transition;
  }
  &.top.opened &__icon {
    transform: rotate(180deg);
  }
  &.bottom.opened &__icon {
    transform: rotate(360deg);
  }
  &.left.opened &__icon {
    transform: rotate(90deg);
  }
  &.right.opened &__icon {
    transform: rotate(-90deg);
  }
  // ICON

  // LIST
  &.opened &__list {
    opacity: 1;
    visibility: visible;
  }
  &.top &__list {
    // top ↑
    top: -10px;
    left: 0;
    transform: translateY(-100%);
  }
  &.bottom &__list {
    // bottom ↓
    bottom: -10px;
    left: 0;
    transform: translateY(100%);
  }
  &.left &__list {
    // left ←
    left: -10px;
    top: 0;
    transform: translateX(-100%);
  }
  &.right &__list {
    // right →
    right: -10px;
    top: 0;
    transform: translateX(100%);
  }
  &__list {
    opacity: 0;
    visibility: hidden;
    position: absolute;
    background-color: rgb(255, 255, 255);
    padding: 18px 0 14px;
    box-shadow: $--shadow;
    border-radius: 3px;
    transition: $--transition;
    &__item {
      cursor: pointer;
      user-select: none;
      font-family: Inter;
      font-size: 13px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      padding: 13px 40px 13px 20px;
      white-space: nowrap;
      position: relative;
      span {
        position: relative;
        z-index: 2;
      }
      &:after {
        content: '';
        display: block;
        position: absolute;
        width: 0;
        height: 2px;
        background-color: $--accent;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        transition: $--transition;
        z-index: 1;
      }
      &:hover:after {
        width: 100%;
      }
      &:active:after {
        height: 100%;
      }
    }
  }
  // LIST
}
</style>
